<template>
  <div>
    <v-autocomplete
      v-model="model"
      :items="items"
      :loading="isLoading"
      :hide-no-data="!strict"
      outlined
      :rules="rules"
      :search-input.sync="search"
      dense
      :item-text="text"
      :filter="customFilter || customFilter1"
      :placeholder="placeholder"
      :label="label"
      return-object
      clearable
    />
  </div>
</template>

<script>
export default {
  props: [
    "text",
    "label",
    "searchFn",
    "value",
    "rules",
    "placeholder",
    "mapFn",
    "strict",
    "customFilter",
  ],

  data() {
    return {
      isLoading: false,
      search: null,
      items: [],
      oldVal: "",
      model: null,
      timeout: null,
      oldArgs: null,
      editMode: false,
    };
  },

  methods: {
    // Normalize Arabic text to match backend logic
    normalizeArabicText(input) {
      if (!input) return "";

      const arabicLetterMap = {
        أ: 'ا',
        إ: 'ا',
        آ: 'ا',
        ٱ: 'ا', // Alif variations
        ى: 'ي',
        ي: 'ي', // Yeh variations
        ة: 'ه', // Taa Marbuta to Haa
        ك: 'ك',
        ﮎ: 'ك',
        ﮏ: 'ك',
        ﮐ: 'ك',
        ﮑ: 'ك', // Kaf variations
        ق: 'ق',
        گ: 'ق', // Qaf variations
        ؤ: 'و',
        ئ: 'ي', // Hamza variations
        ﷲ: 'ل', // Ligature Allah to Lam
        پ: 'ب',
        چ: 'ج',
        ژ: 'ج',
        ڤ: 'ف' // Persian/Urdu letters
      };

      let normalized = "";
      for (const char of input) {
        normalized += arabicLetterMap[char] || char;
      }

      // Remove Arabic diacritics (U+064B to U+065F, U+0670) and tatweel (U+0640)
      normalized = normalized.replace(/[\u064B-\u065F\u0670]/g, "").replace(/\u0640/g, "");
      return normalized;
    },

    reset() {
      this.search = null;
    },

    setModel(model) {
      if (!model) {
        model = {};
      } else {
        this.editMode = true;
        this.model = model;
        this.items.push(model);
        this.oldVal = model[this.text];
        this.search = model[this.text];
        const self = this;
        setTimeout(() => {
          self.editMode = false;
        }, 200);
      }
    },

    debounce(fn, args, time) {
      clearTimeout(this.timeout);
      const self = this;
      this.timeout = setTimeout(() => {
        if (self.oldArgs == args) return;
        this.oldArgs = args;
        fn(args);
      }, time);
    },

    internalSearch(val) {
      if (val == this.oldVal) return;
      this.oldVal = val;
      this.isLoading = true;
      if (!this.searchFn) return;

      console.log(`Searching for: ${val}`);
      this.searchFn(val)
        .then((resp) => {
          console.log('Backend response:', resp.data);
          if (this.mapFn) {
            this.items = resp.data.map((e) => this.mapFn(e));
          } else {
            this.items = resp.data;
          }
          if (!this.items) this.items = [];
          if (this.items.length == 0) {
            console.log(`No matches for: ${val}`);
            if (val == null) {
              this.model = {};
              return;
            }
            const obj = {};
            obj[this.text] = `${this.search}`;
            this.items.push(obj);
            this.model = this.clone(obj);
          }
        })
        .catch((err) => {
          console.error('Search error:', err);
        })
        .finally(() => {
          this.isLoading = false;
          this.$emit("input", this.clone(this.model));
        });
    },

    customFilter1(item, queryText, itemText) {
      if (!item[this.text] || !queryText) return false;
      const normalizedItemText = this.normalizeArabicText(item[this.text]).toLowerCase();
      const normalizedQueryText = this.normalizeArabicText(queryText).toLowerCase();
      console.log(`Comparing: ${normalizedItemText} contains ${normalizedQueryText}`); // Improved logging
      return normalizedItemText.includes(normalizedQueryText) || item[this.text] === queryText;
    },

    // Utility to clone objects
    clone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
  },

  watch: {
    search(value) {
      if (this.editMode) return;
      if (
        value == this.oldVal
        || (this.model && this.model[this.text] == value)
      ) return;
      return this.debounce(
        this.internalSearch.bind(this),
        this.clone(value),
        250
      );
    },
    model(newVal, oldVal) {
      this.$emit("input", this.clone(this.model));
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .theme--light.v-list-item {
  color: rgba(0, 0, 0, 0.87) !important;
  text-align: right !important;
}
</style>
